import {
  Breadcrumbs,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  OrganizationChooser,
  PageContainer,
  Skeleton,
} from '@startupquickstart/frontend';
import { Smartphone, Building2 } from 'lucide-react';
import { useOrganizationList } from '@clerk/clerk-react';

import { AppStoreBadges } from '@/components/shared';

/**
 * Landing page for users whose active context is a personal account (no org).
 * Surfaces the mobile download links, and — when the signed-in user belongs to
 * one or more organizations — an OrganizationSwitcher to enter the web portal.
 *
 * Org membership is read directly from Clerk (`useOrganizationList`) rather than
 * a backend call; the previous `/users/me` fetch hit an endpoint that doesn't
 * exist and always resolved to "no organizations".
 */
export function PersonalAccountHome() {
  const breadcrumbs = [{ label: 'Home', current: true }];

  const { isLoaded, userMemberships } = useOrganizationList({ userMemberships: true });
  const isLoading = !isLoaded;
  const hasAvailableOrganizations = (userMemberships?.count ?? 0) > 0;

  return (
    <PageContainer>
      <Breadcrumbs items={breadcrumbs} className="mb-4" />

      <div className="flex flex-col items-center justify-center min-h-[60vh] max-w-2xl mx-auto">
        <Card className="w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
                <Smartphone className="w-10 h-10 text-primary" />
              </div>
            </div>
            <CardTitle className="text-3xl">Welcome to Hunts!</CardTitle>
            <CardDescription className="text-base mt-2">
              The web portal is currently not available for personal accounts
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : hasAvailableOrganizations ? (
              <>
                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-left flex-1">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                        Organization Access Available
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
                        Choose an organization to open its portal:
                      </p>
                      <OrganizationChooser />
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground mb-2">
                    Or access your content via our mobile app:
                  </p>
                </div>
              </>
            ) : (
              <p className="text-muted-foreground">
                To participate in scavenger hunts and challenges, please download and use our
                mobile app.
              </p>
            )}

            <AppStoreBadges className="justify-center gap-4 pt-4" />

            {!hasAvailableOrganizations && (
              <div className="pt-6 border-t">
                <p className="text-sm text-muted-foreground">
                  If you believe you should have access to the web portal, please contact your
                  organization administrator.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
